import type { ConfigProps } from '../../configTypes';
import { normalizeSiteUrl } from '../lib/normalizeSiteUrl';
import { resolveRobots } from './resolveRobots';

export type TransformedSeo = {
  title: string;
  description: string;
  url: string;
  robots: string;
  imageUrl?: string;
  imageWidth?: string;
  imageHeight?: string;
  imageType?: string;
  type?: string;
  locale?: string;
  siteName?: string;
  pageHeadCode?: string;
  pageFooterCode?: string;
  aboutType?: string;
  aboutName?: string;
  twitterCard?: string;
  jsonLd?: string;
  image?: string;
};

type TransformedSeoProps = {
  generalSettings: Record<string, string> | undefined;
  pageSeo?: {
    title?: string;
    description?: string;
    canonicalUrl?: string;
    openGraph?: {
      title?: string;
      description?: string;
      image?: { url?: string; width?: number; height?: number; type?: string };
      type?: string;
      locale?: string;
      twitterMeta?: { card?: string };
    };
    pageHeadCode?: string;
    pageFooterCode?: string;
    aboutType?: string;
    aboutName?: string;
    jsonLd?: { raw?: string };
    robots?: unknown;
  };
  config: ConfigProps;
  uri: string;
};

export default function transformSeo({
  generalSettings,
  pageSeo,
  config,
  uri,
}: TransformedSeoProps): TransformedSeo {
  const modifiedUri =
    (uri.startsWith('/') ? '' : '/') + uri + (uri.endsWith('/') ? '' : '/');
  const baseSiteUrl = normalizeSiteUrl(config.siteUrl, config.domain);
  const isProd =
    (process.env as Record<string, string>)['ENVIRONMENT'] === 'prd';
  const customRobots = resolveRobots({
    isProd,
    cmsRobots: pageSeo?.robots,
  });

  if (!pageSeo) {
    return {
      title: generalSettings?.['title'] || config.domainLabelWithExtension,
      description: generalSettings?.['description'] || '',
      url: `${baseSiteUrl}${modifiedUri}`,
      image: '',
      robots: customRobots,
    };
  }

  const seoTitle =
    pageSeo.openGraph?.['title'] ||
    pageSeo['title'] ||
    generalSettings?.['title'];
  const seoDescription =
    pageSeo.openGraph?.['description'] ||
    pageSeo['description'] ||
    generalSettings?.['description'];
  const rawURL = pageSeo['canonicalUrl'] || baseSiteUrl + modifiedUri;
  const siteURL = replaceDomain(rawURL, baseSiteUrl);
  const url = stripHomepageSlug(siteURL, config.homepageGql);

  return {
    title: decodeHtmlEntities(seoTitle ?? ''),
    description: decodeHtmlEntities(seoDescription ?? ''),
    url,
    imageUrl: pageSeo.openGraph?.image?.['url'] || '',
    imageWidth: String(pageSeo.openGraph?.image?.['width'] ?? '') || '',
    imageHeight: String(pageSeo.openGraph?.image?.['height'] ?? '') || '',
    imageType: pageSeo.openGraph?.image?.['type'] || '',
    type: pageSeo.openGraph?.['type'] || '',
    locale: pageSeo.openGraph?.['locale'] || '',
    siteName: config.domainLabelWithExtension,
    pageHeadCode: pageSeo.pageHeadCode || '',
    pageFooterCode: pageSeo.pageFooterCode || '',
    ...(pageSeo.aboutType ? { aboutType: pageSeo.aboutType } : {}),
    ...(pageSeo.aboutName ? { aboutName: pageSeo.aboutName } : {}),
    robots: customRobots,
    twitterCard: pageSeo.openGraph?.twitterMeta?.card || '',
    jsonLd: pageSeo.jsonLd?.raw || '',
  };
}

function stripHomepageSlug(
  siteURL: string,
  homepageGql: string | undefined,
): string {
  const raw = (homepageGql ?? '').trim();
  if (!raw || raw === '/') return siteURL;

  const withLeading = raw.startsWith('/') ? raw : `/${raw}`;
  const homepagePath = `${withLeading.replace(/\/+$/, '')}/`;

  try {
    const parsed = new URL(siteURL);
    const path = parsed.pathname.endsWith('/')
      ? parsed.pathname
      : `${parsed.pathname}/`;
    if (path === homepagePath) {
      parsed.pathname = '/';
      return parsed.href;
    }
    return siteURL;
  } catch {
    return siteURL;
  }
}

function replaceDomain(originalUrl: string, destinationUrl: string): string {
  try {
    const url = new URL(normalizeSiteUrl(originalUrl));
    const newUrl = new URL(destinationUrl);
    newUrl.search = url.search;
    newUrl.pathname = url.pathname;
    return newUrl.href;
  } catch {
    return destinationUrl;
  }
}

function decodeHtmlEntities(str: string): string {
  if (!str) return '';
  return str
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(Number(dec)))
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&hellip;/g, '...')
    .replace(/&amp;/g, '&');
}
